import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import {
  User as UserIcon, Bell, Settings, CreditCard, Gift, Users, HelpCircle,
  LogOut, Shield, Download, Trash2, ChevronRight, ExternalLink, FolderDown,
  BookOpen,
} from "lucide-react";

/**
 * AccountMenu — top-right avatar dropdown.
 * Rows are declared here but visibility is controlled from Admin Console →
 * Menu Directory (section "Profile Menu") — the same Menu Directory that
 * governs sidebar items. Every entry has an `id` matching a Menu Directory
 * item id (`account-menu-*`) so admin toggles apply without a redeploy.
 */
export default function AccountMenu() {
  const { user, logout } = useAuth();
  const { isDisabled } = useMenuVisibility();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const avatarUrl = user?.picture;
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  const handleLogout = async () => {
    setOpen(false);
    try { await logout(); } catch { /* no-op */ }
    nav("/", { replace: true });
  };
  const go = (path) => () => { setOpen(false); nav(path); };

  const rawItems = [
    { key: "profile",       icon: UserIcon,    label: "Profile & Settings",     onClick: go("/account") },
    { key: "notifications", icon: Bell,        label: "Notification Preferences", onClick: go("/account/notifications") },
    { key: "subscription",  icon: CreditCard,  label: "Subscription & Billing", onClick: go("/account/subscription") },
    { key: "downloads",     icon: FolderDown,  label: "Download Library",       onClick: go("/downloads") },
    // Referral Rewards lives inside the tabbed Account & Access page —
    // deep-link straight to that tab using `?tab=referrals` (the Account
    // page reads this on mount) so the founder lands on the right screen
    // instead of the default Profile tab.
    { key: "referrals",     icon: Gift,        label: "Refer a friend",         onClick: go("/account?tab=referrals") },
    { key: "team",          icon: Users,       label: "Team Seats",             onClick: go("/account?tab=subscription") },
    { key: "faq",           icon: BookOpen,    label: "FAQ",                    onClick: go("/faq") },
    { key: "help",          icon: HelpCircle,  label: "Help & Walkthrough",     onClick: go("/walkthrough") },
  ];
  const rawDangerItems = [
    { key: "data",          icon: Download,    label: "Export my data",         onClick: go("/account#data-export") },
    { key: "delete",        icon: Trash2,      label: "Delete account",         onClick: go("/account#danger-zone") },
  ];
  // Filter every row against Menu Directory. Admin toggles the Profile Menu
  // items from Admin Console → Menu Directory (section "Profile Menu") and
  // they disappear here without a redeploy.
  const items = rawItems.filter((it) => !isDisabled(`account-menu-${it.key}`));
  const dangerItems = rawDangerItems.filter((it) => !isDisabled(`account-menu-${it.key}`));
  const adminVisible = !isDisabled("account-menu-admin");
  const signoutVisible = !isDisabled("account-menu-signout");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="topbar-account-trigger"
        className="w-9 h-9 rounded-full bg-shell-side text-white grid place-items-center font-bold text-xs overflow-hidden hover:ring-2 hover:ring-primary/40 transition"
      >
        {avatarUrl
          ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          : initials}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="account-menu-panel"
          className="absolute right-0 mt-2 w-64 rounded-2xl border border-shell-border bg-white text-foreground shadow-2xl z-50 overflow-hidden"
        >
          <div className="p-3 border-b border-shell-border/70">
            <p className="font-semibold text-sm truncate" data-testid="account-menu-name">{user?.name || "Founder"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email || ""}</p>
            <div className="mt-1 flex items-center gap-1">
              {user?.role && user.role !== "user" && (
                <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                  <Shield className="w-2.5 h-2.5 mr-1" /> {(user?.role || "admin").replace("_", " ")}
                </span>
              )}
              {user?.package_slug && (
                <span className="inline-flex rounded-full border border-shell-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {user.package_slug}
                </span>
              )}
            </div>
          </div>

          {items.length > 0 && (
            <ul className="py-1.5">
              {items.map((it) => (
                <li key={it.key}>
                  <button
                    type="button"
                    onClick={it.onClick}
                    role="menuitem"
                    data-testid={`account-menu-${it.key}`}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-shell-muted/60 transition text-left"
                  >
                    <it.icon className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1">{it.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {adminVisible && ["super_admin", "admin", "contributor"].includes((user?.role || "").toLowerCase()) && (
            <>
              <div className="border-t border-shell-border" />
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="account-menu-admin"
                className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-shell-muted/60 transition"
              >
                <Settings className="w-4 h-4 text-primary" />
                <span className="flex-1 font-semibold text-primary">Admin Console</span>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60" />
              </Link>
            </>
          )}

          {dangerItems.length > 0 && (
            <>
              <div className="border-t border-shell-border" />
              <ul className="py-1.5">
                {dangerItems.map((it) => (
                  <li key={it.key}>
                    <button
                      type="button"
                      onClick={it.onClick}
                      role="menuitem"
                      data-testid={`account-menu-${it.key}`}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-shell-muted/60 transition text-left text-muted-foreground"
                    >
                      <it.icon className="w-4 h-4" />
                      <span className="flex-1">{it.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {signoutVisible && (
            <>
              <div className="border-t border-shell-border" />
              <button
                type="button"
                onClick={handleLogout}
                role="menuitem"
                data-testid="account-menu-signout"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-rose-50 transition text-rose-700 font-semibold"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
